import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchCurrencySelectOptions } from '#/api/system/currency';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import useSideDetail from '#/hooks/useSideDetail';
import { createAsyncFilterLabelCache } from '#/modules/commonComponents/table/asyncFilterLabelCache';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';
import PurchaseOrderHubHeader from '#/modules/srmMgmt/purchaseOrder/components/PurchaseOrderHubHeader';
import PurchaseOrderSideMsg from '#/modules/srmMgmt/purchaseOrder/components/PurchaseOrderSideMsg';
import { buildPurchaseOrderColumns } from '#/modules/srmMgmt/purchaseOrder/data/purchaseOrderColumns';
import {
  applyPurchaseOrderDateRangeToFilters,
} from '#/modules/srmMgmt/purchaseOrder/helper/purchaseOrderDateListFilter';
import { getPurchaseOrderApiErrorMessage } from '#/modules/srmMgmt/purchaseOrder/helper/apiError';
import {
  hydrateSupplierFilterLabels,
} from '#/modules/srmMgmt/supplier/helper/searchSupplierFilterOptions';
import usePurchaseOrderList from '#/modules/srmMgmt/purchaseOrder/hooks/usePurchaseOrderList';
import { lookupQueryKey } from '#/utils/queryKeys';

const PURCHASE_ORDER_SORT_FIELD_LABELS = {
  ...DEFAULT_LIST_SORT_FIELD_LABELS,
  purchaseOrderNo: '採購單號',
  status: '狀態',
  createdAt: '建立日期',
  closedAt: '結案日期',
};

const PurchaseOrderListPage = () => {
  const listRef = useRef(null);
  const { displayData, setSelectedData } = useSideDetail();
  const canCreate = useCan(PERM.PURCHASE_ORDER_CREATE);
  const canUpdate = useCan(PERM.PURCHASE_ORDER_UPDATE);

  const [supplierLabelCache] = useState(createAsyncFilterLabelCache);
  const [labelCacheVersion, setLabelCacheVersion] = useState(0);

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
    setListQuery,
  } = usePurchaseOrderList();

  useEffect(() => {
    let cancelled = false;

    hydrateSupplierFilterLabels(listQuery.filters?.supplierId, supplierLabelCache)
      .then((didHydrate) => {
        if (!cancelled && didHydrate) {
          setLabelCacheVersion((version) => version + 1);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    listQuery.filters?.supplierId,
    supplierLabelCache,
  ]);

  // 起迄須同一次寫入 URL；連續兩次 setFilter 會以舊 listQuery 為底，後者蓋掉前者
  const setPurchaseOrderDateRangeFilter = useCallback((range) => {
    setListQuery((prev) => ({
      ...prev,
      page: 1,
      filters: applyPurchaseOrderDateRangeToFilters(prev.filters, range),
    }));
  }, [setListQuery]);

  const currencyOptionsQuery = useQuery({
    queryKey: lookupQueryKey('currencySelect'),
    queryFn: fetchCurrencySelectOptions,
  });
  const currencyOptions = currencyOptionsQuery.data;

  const columns = useMemo(
    () => {
      void labelCacheVersion;
      return buildPurchaseOrderColumns({
        filters: listQuery.filters || {},
        onFilterChange: setFilter,
        onPurchaseOrderDateRangeChange: setPurchaseOrderDateRangeFilter,
        supplierLabelCache,
        currencyOptions: currencyOptions ?? [],
        canShowEdit: (row) => canUpdate && row.status === 'DRAFT',
      });
    },
    [
      listQuery.filters,
      setFilter,
      setPurchaseOrderDateRangeFilter,
      supplierLabelCache,
      labelCacheVersion,
      canUpdate,
      currencyOptions,
    ],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns, { filters: listQuery.filters }),
    [columns, listQuery.filters],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const handleRowClick = useCallback((row, isSelected) => {
    if (isSelected) {
      setSelectedData(row.original);
      return;
    }
    setSelectedData(null);
  }, [setSelectedData]);

  const headerAction = canCreate ? (
    <ListAddButton to={ROUTES.purchaseOrderCreate} />
  ) : null;

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <PurchaseOrderHubHeader page="material" />
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              ref={listRef}
              tableKey="srm.purchaseOrder.list"
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={
                isError
                  ? getPurchaseOrderApiErrorMessage(error, '載入列表失敗')
                  : undefined
              }
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋採購單號、供應商或備註"
              listQuery={listQuery}
              filterStatusFields={filterStatusFields}
              sortFieldLabels={PURCHASE_ORDER_SORT_FIELD_LABELS}
              onFilterChange={setFilter}
              onKeywordClear={() => setKeyword('')}
              onSortRemove={removeSortAt}
              onSortingChange={handleSortingChange}
              headerAction={headerAction}
            />
          </div>

          <PurchaseOrderSideMsg
            displayData={displayData}
            setSelectedData={setSelectedData}
          />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default PurchaseOrderListPage;
